import type {
  InsCheckTypeValue,
  InsEventTypeValue,
  InsStatusCheckTypeValue,
} from '../types/ins.types'
import { ComparisonResultItem } from '../../../model/ComparisonResult.type'
import type { InsiPatient } from '../../../model/Insi'
import type { PatientFormFields } from '../../../components/patient/PatientForm/PatientForm.model'
import { ComparisonStatus } from '../../../model/ComparisonStatus.enum'
import { inputInseeNumberFormatter } from '../../../misc/inseeNumber.utils'
import { ResourceHistoryMutationPayload } from '../../../data/resourceHistory/mapper'
import type { LightPatient, Patient } from '../../../model/Patient'
import { IdentityStatusLabels } from '../enums/insStatusCheckLabels.enums'
import { IdentityStatusMessages } from '../enums/insStatusCheckMessages.enums'

export default class InsUtilities {
  static generateNewInsPatientResourceHistory({
    eventType,
    patientId,
    status,
    checkType,
    extraData,
  }: {
    eventType: InsEventTypeValue
    patientId: string
    status: InsStatusCheckTypeValue | string
    checkType: InsCheckTypeValue | string
    extraData?: Record<string, string>
  }): ResourceHistoryMutationPayload {
    return {
      eventType,
      resourceId: patientId,
      resourceType: 'Patient',
      extraData: {
        ...extraData,
        insCheckType: checkType,
        insChecked: status,
      },
    }
  }

  static extractComparisonDifferences(
    comparedValues: ComparisonResultItem<
      InsiPatient & {
        otherFirstNames?: string | undefined
      } & PatientFormFields,
      string
    >[],
    fieldMappings: string[],
  ) {
    return comparedValues
      .filter(
        (item) =>
          fieldMappings.includes(item.key) &&
          [
            ComparisonStatus.Different,
            ComparisonStatus.EmptyInA,
            ComparisonStatus.EmptyInB,
            ComparisonStatus.MissingInA,
            ComparisonStatus.MissingInB,
          ].includes(item.status),
      )
      .reduce<Record<string, string>>((acc, item) => {
        if (item.key === 'assigningAuthority') {
          acc[item.key] = (item.valueA as { label: string; oid: string })?.oid
        } else if (item.key === 'inseeNumber') {
          acc[item.key] = inputInseeNumberFormatter(item.valueA as string)
        } else {
          acc[item.key] = item.valueA as string
        }
        return acc
      }, {})
  }

  static shouldLogDifferences(
    comparedValues: ComparisonResultItem<
      InsiPatient & {
        otherFirstNames?: string | undefined
      } & PatientFormFields,
      string
    >[],
    fieldMappings: string[],
  ): boolean {
    return comparedValues.some(
      (item) =>
        fieldMappings.includes(item.key) &&
        [
          ComparisonStatus.Different,
          ComparisonStatus.EmptyInA,
          ComparisonStatus.EmptyInB,
          ComparisonStatus.MissingInA,
          ComparisonStatus.MissingInB,
        ].includes(item.status),
    )
  }

  static isQualified(patient: Patient | LightPatient): boolean {
    if (!patient?.identityStatus) return false

    return patient?.identityStatus?.state === 'QUALIFIED'
  }

  static  getIdentityStatusLabel(value: string): string {
    return IdentityStatusLabels[value] ?? value;
  }
  static  getIdentityStatusMessage(value: string): string {
    return IdentityStatusMessages[value] ?? value;
  }
}
